import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { GetRPNListParams } from '@nxsys/store/slices/rpn/types';
import { getRangeYear, getTaxWeekTaxYear } from '@nxsys/utils/taxYear';
import { hasFilterValue } from '@nxsys/utils/filterForm';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: GetRPNListParams) => void;
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();

  const {
    rpnList: { queryParams },
  } = useSelector((state: AppState) => state.rpnStore);
  const { isAdmin, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const taxYearOptions: OptionType[] = taxYearRange.map((option) => ({
    label: option.value.toString(),
    value: option.value.toString(),
  }));

  const filterOptions = [
    {
      label: messages['common.taxYear'],
      name: 'taxYear',
      filterType: 'select',
      options: taxYearOptions,
      placeholder: messages['common.selectTaxYear'],
      defaultValue: taxYearOptions.find(
        (option: OptionType) =>
          option.value === getTaxWeekTaxYear().taxYear.toString(),
      ),
      allowClear: false,
    },
    ...(isAdmin
      ? [
          {
            label: employerLabels.label,
            name: 'companyList',
            filterType: 'selectSync',
            selectSyncType: 'company',
            apiType: 'searchCompany',
            placeholder: employerLabels.placeholder,
          },
        ]
      : []),
    {
      label: messages['common.effectiveDate'],
      name: 'effectiveDate',
      filterType: 'date',
      placeholder: messages['common.selectDate'],
    },
    {
      label: messages['common.endDate'],
      name: 'endDate',
      filterType: 'date',
      placeholder: messages['common.selectDate'],
    },
    {
      label: messages['rpn.rpnIssueDate'],
      name: 'rpnIssueDate',
      filterType: 'date',
      placeholder: messages['common.selectDate'],
    },
    {
      label: messages['common.lastUpdated'],
      name: 'updatedAt',
      filterType: 'date',
      placeholder: messages['common.selectDate'],
    },
  ];

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params = {
      ...queryParams,
      page: 0,
      effectiveDate: values?.effectiveDate
        ? moment(values?.effectiveDate).format('YYYY-MM-DD')
        : undefined,
      endDate: values?.endDate
        ? moment(values?.endDate).format('YYYY-MM-DD')
        : undefined,
      rpnIssueDate: values?.rpnIssueDate
        ? moment(values?.rpnIssueDate).format('YYYY-MM-DD')
        : undefined,
      updatedAt: values?.updatedAt
        ? moment(values?.updatedAt).format('YYYY-MM-DD')
        : undefined,
      companyIds: values?.companyList
        ? values?.companyList?.map((com: any) => com.value)
        : undefined,
      taxYears: values?.taxYear ? [values?.taxYear] : undefined,
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // Failed validation handled by Ant Design Form
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {filterOptions?.map((f) => {
          return (
            <Form.Item
              key={f.name}
              label={f.label}
              name={f.name}
              layout="vertical"
            >
              {f.filterType === 'select' && (
                <AppSelect
                  placeholder={f.placeholder}
                  options={f.options ?? []}
                  onChange={(value) => {
                    form.setFieldsValue({
                      [f.name]: value.value,
                    });
                  }}
                  {...(f?.defaultValue && { defaultValue: f?.defaultValue })}
                  {...(f?.allowClear !== undefined && {
                    allowClear: f?.allowClear,
                  })}
                />
              )}
              {f.filterType === 'selectSync' && (
                <AppSelectSync
                  type={f.selectSyncType ?? ''}
                  apiType={f.apiType as any}
                  placeholder={f.placeholder}
                  onChangeValue={(value: OptionType | OptionType[] | null) => {
                    form.setFieldsValue({
                      [f.name]: value,
                    });
                  }}
                  mode={'multiple'}
                />
              )}
              {f.filterType === 'date' && (
                <AppDatePicker
                  placeholder={f.placeholder}
                  onChange={(date: Moment) => {
                    form.setFieldsValue({
                      [f.name]: date.toISOString(),
                    });
                  }}
                  format="DD/MM/YYYY"
                />
              )}
            </Form.Item>
          );
        })}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
